import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Skeleton,
} from '@mui/material';
import { Close, Delete, FiberManualRecord, PlayArrow } from '@mui/icons-material';
import { FC } from 'react';

import { useDeleteVideoMutation } from '../../store/jobseeker/video.api';

interface VideoViewModalProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string | null;
  questionId: number | null;
  jobseekerId: number | null;
  questionText?: string;
  onDelete: (videoUrl: string) => void;
  onRerecord: (questionId: number) => void;
}

export const VideoViewModal: FC<VideoViewModalProps> = ({
  open,
  onClose,
  videoUrl,
  questionId,
  jobseekerId,
  questionText,
  onDelete,
  onRerecord,
}) => {
  const [deleteVideo] = useDeleteVideoMutation();

  const handleDelete = async () => {
    if (videoUrl && questionId && jobseekerId) {
      try {
        await deleteVideo({ jobseekerId, questionId }).unwrap();
        onDelete(videoUrl);
        onClose();
      } catch (error) {
        console.error('Failed to delete video:', error);
      }
    }
  };

  const handleRerecord = () => {
    if (questionId) {
      onRerecord(questionId);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: '90vh',
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
            <PlayArrow sx={{ mr: 1 }} />
            Video Answer
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        {questionText && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pr: { xs: 4, sm: 0 } }}>
            {questionText}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16/9',
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
            backgroundColor: 'grey.100',
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {videoUrl ? (
            <video
              controls
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              src={videoUrl}
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <PlayArrow sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No video available
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > :not(style)': { width: { xs: '100%', sm: 'auto' } },
        }}
      >
        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          onClick={handleDelete}
          disabled={!videoUrl}
          sx={{ minWidth: { xs: 'auto', sm: 'fit-content' } }}
        >
          Delete Video
        </Button>

        <Button
          variant="contained"
          startIcon={<FiberManualRecord />}
          onClick={handleRerecord}
          disabled={!videoUrl}
          sx={{ minWidth: { xs: 'auto', sm: 'fit-content' } }}
        >
          Re-record Answer
        </Button>
      </DialogActions>
    </Dialog>
  );
};
